import React, { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    User as UserIcon, Store as StoreIcon, ImagePlus, FileText,
    ArrowLeft, ArrowRight, Check, Loader2, Upload, X, Sparkles,
    Pizza, Beef, Coffee, Soup, Salad, Cookie
} from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";

const STEPS = [
    { id: "account", title: "Λογαριασμός",  icon: UserIcon },
    { id: "store",   title: "Κατάστημα",    icon: StoreIcon },
    { id: "media",   title: "Φωτογραφίες",  icon: ImagePlus },
    { id: "menu",    title: "Μενού",         icon: FileText },
    { id: "done",    title: "Έτοιμο",        icon: Sparkles },
];

const CUISINES = [
    { id: "burger",  label: "Burger", icon: Beef },
    { id: "pizza",   label: "Pizza",  icon: Pizza },
    { id: "asian",   label: "Asian",  icon: Soup },
    { id: "coffee",  label: "Coffee", icon: Coffee },
    { id: "street",  label: "Street", icon: Salad },
    { id: "dessert", label: "Γλυκά",  icon: Cookie },
];

const MENU_CATS = ["burgers", "sides", "drinks", "desserts", "extras", "pizza", "sushi", "salads", "mains"];

// === CSV parser (simple, supports quoted fields) ===
const parseCSV = (text) => {
    const lines = text.split(/\r?\n/).filter((l) => l.trim().length > 0);
    if (!lines.length) return [];
    const split = (line) => {
        const cols = []; let cur = ""; let q = false;
        for (let i = 0; i < line.length; i++) {
            const c = line[i];
            if (c === '"') { q = !q; continue; }
            if (c === "," && !q) { cols.push(cur.trim()); cur = ""; continue; }
            cur += c;
        }
        cols.push(cur.trim());
        return cols;
    };
    const headers = split(lines[0]).map((h) => h.toLowerCase().trim());
    return lines.slice(1).map((l) => {
        const cols = split(l);
        const row = {};
        headers.forEach((h, i) => { row[h] = cols[i] || ""; });
        return row;
    });
};

const fileToDataUrl = (file) => new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
});

export const PartnerSignup = () => {
    const navigate = useNavigate();
    const { setUserDirect } = useAuth();
    const [step, setStep] = useState(0);
    const [submitting, setSubmitting] = useState(false);

    // Form state
    const [account, setAccount] = useState({
        owner_name: "", email: "", password: "", phone: "",
    });
    const [store, setStore] = useState({
        store_name: "", cuisine: "burger", tagline: "",
        address: "", lat: 40.6336, lng: 22.9430,
        delivery_minutes: 30, delivery_fee: 2.5,
    });
    const [media, setMedia] = useState({ logo_url: "", image_url: "" });
    const [menuItems, setMenuItems] = useState([]); // [{name, description, price, category, image_url}]
    const [csvError, setCsvError] = useState("");

    const fileLogoRef = useRef(null);
    const fileImageRef = useRef(null);
    const fileCsvRef = useRef(null);

    // === Step 1 validation ===
    const canNext = () => {
        if (step === 0) {
            return account.owner_name.length > 1 &&
                /\S+@\S+\.\S+/.test(account.email) &&
                account.password.length >= 6;
        }
        if (step === 1) {
            return store.store_name.length > 1 && store.address.length > 2;
        }
        if (step === 2) return true; // photos optional
        if (step === 3) return true; // menu optional
        return true;
    };

    const handleLogo = async (e) => {
        const f = e.target.files?.[0]; if (!f) return;
        if (f.size > 2 * 1024 * 1024) { toast.error("Λογότυπο < 2MB"); return; }
        const url = await fileToDataUrl(f);
        setMedia((m) => ({ ...m, logo_url: url }));
    };
    const handleImage = async (e) => {
        const f = e.target.files?.[0]; if (!f) return;
        if (f.size > 4 * 1024 * 1024) { toast.error("Φωτό < 4MB"); return; }
        const url = await fileToDataUrl(f);
        setMedia((m) => ({ ...m, image_url: url }));
    };

    const handleCsv = async (e) => {
        setCsvError("");
        const f = e.target.files?.[0]; if (!f) return;
        try {
            const text = await f.text();
            const rows = parseCSV(text);
            if (!rows.length) { setCsvError("Άδειο αρχείο"); return; }
            const items = rows.slice(0, 200).map((r) => ({
                name: r.name || r.όνομα || "",
                description: r.description || r.περιγραφή || "",
                price: parseFloat((r.price || r.τιμή || "0").replace(",", ".")) || 0,
                category: MENU_CATS.includes((r.category || "").toLowerCase()) ? (r.category || "").toLowerCase() : "mains",
                image_url: r.image_url || r.image || "",
            })).filter((it) => it.name && it.price > 0);
            if (!items.length) { setCsvError("Δεν βρέθηκαν έγκυρες γραμμές. Στήλες: name, description, price, category, image_url"); return; }
            setMenuItems(items);
            toast.success(`Φορτώθηκαν ${items.length} προϊόντα`);
        } catch (err) {
            setCsvError("Σφάλμα ανάγνωσης CSV");
        }
    };

    const submit = async () => {
        setSubmitting(true);
        try {
            // 1. Create the partner account + store
            const { data } = await api.post("/partner/signup", {
                owner_name: account.owner_name,
                email: account.email,
                password: account.password,
                phone: account.phone,
                store_name: store.store_name,
                cuisine: store.cuisine,
                tagline: store.tagline,
                address: store.address,
                lat: parseFloat(store.lat),
                lng: parseFloat(store.lng),
                delivery_minutes: parseInt(store.delivery_minutes, 10),
                delivery_fee: parseFloat(store.delivery_fee),
                logo_url: media.logo_url,
                image_url: media.image_url || media.logo_url,
            });
            // 2. Persist auth token + user
            localStorage.setItem("sh_token", data.token);
            setUserDirect?.(data.user);
            // 3. Bulk insert menu items if any
            if (menuItems.length > 0) {
                try {
                    await api.post("/partner/menu/bulk", {
                        items: menuItems.map((it) => ({ ...it, available: true, featured: false })),
                    }, { headers: { Authorization: `Bearer ${data.token}` } });
                } catch (err) {
                    toast.error("Δεν φορτώθηκαν τα προϊόντα μενού");
                }
            }
            setStep(STEPS.length - 1);
            toast.success("Εγγραφή ολοκληρώθηκε! Καλώς ήρθες στο Thess·Eats.");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Σφάλμα εγγραφής");
        } finally {
            setSubmitting(false);
        }
    };

    const goNext = () => {
        if (step === 3) { submit(); return; }
        if (step < STEPS.length - 1) setStep(step + 1);
    };

    return (
        <div data-testid="partner-signup" className="min-h-screen cyber-bg text-white pb-12">
            {/* HEADER + PROGRESS */}
            <header className="sticky top-0 z-30 cyber-bg/95 backdrop-blur-xl border-b border-white/5 px-5 py-4">
                <div className="flex items-center justify-between mb-3">
                    <button onClick={() => step === 0 ? navigate("/") : setStep(step - 1)} data-testid="signup-back" className="h-10 w-10 grid place-items-center rounded-full glass-gold border border-[var(--gold)]/20" disabled={step === STEPS.length - 1}>
                        <ArrowLeft size={16} className="text-[var(--gold-light)]" />
                    </button>
                    <div className="text-center">
                        <p className="text-[9px] font-mono uppercase tracking-[0.32em] text-white/50">// γίνε partner</p>
                        <h1 className="font-heading text-lg" style={{ fontFamily: '"Cormorant Garamond", serif' }}>{STEPS[step].title}</h1>
                    </div>
                    <span className="font-mono text-[10px] text-white/50 tracking-widest">{step + 1}/{STEPS.length}</span>
                </div>
                {/* Progress steps */}
                <div className="flex items-center gap-1.5">
                    {STEPS.map((s, i) => (
                        <div key={s.id} className={`flex-1 h-1 rounded-full transition ${i <= step ? "bg-gradient-to-r from-[var(--gold)] to-[var(--gold-light)]" : "bg-white/10"}`} />
                    ))}
                </div>
            </header>

            <div className="max-w-xl mx-auto px-5 py-6">
                {/* === STEP 0: ACCOUNT === */}
                {step === 0 && (
                    <section data-testid="step-account" className="space-y-4 animate-fade-up">
                        <p className="text-sm text-white/70">
                            Δημιούργησε τον λογαριασμό owner. Με αυτόν θα συνδέεσαι στο Partner POS και στα στατιστικά σου.
                        </p>
                        <Field label="Όνομα Owner" testid="f-owner-name" value={account.owner_name} onChange={(v) => setAccount({...account, owner_name: v})} placeholder="Νίκος Πετρίδης" />
                        <Field label="Email" testid="f-email" type="email" value={account.email} onChange={(v) => setAccount({...account, email: v})} placeholder="owner@store.gr" />
                        <Field label="Κωδικός" testid="f-password" type="password" value={account.password} onChange={(v) => setAccount({...account, password: v})} placeholder="τουλάχιστον 6 χαρακτήρες" />
                        <Field label="Τηλέφωνο" testid="f-phone" value={account.phone} onChange={(v) => setAccount({...account, phone: v})} placeholder="+30 23..." optional />
                    </section>
                )}

                {/* === STEP 1: STORE === */}
                {step === 1 && (
                    <section data-testid="step-store" className="space-y-4 animate-fade-up">
                        <Field label="Όνομα Καταστήματος" testid="f-store-name" value={store.store_name} onChange={(v) => setStore({...store, store_name: v})} placeholder="Σουβλάκια του Νίκου" />
                        <div>
                            <p className="text-[10px] font-mono uppercase tracking-widest text-white/50 mb-2">Κουζίνα</p>
                            <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
                                {CUISINES.map((c) => {
                                    const Icon = c.icon;
                                    const on = store.cuisine === c.id;
                                    return (
                                        <button
                                            key={c.id}
                                            data-testid={`cuisine-${c.id}`}
                                            onClick={() => setStore({...store, cuisine: c.id})}
                                            className={`flex flex-col items-center gap-1 py-3 rounded-2xl border transition ${
                                                on
                                                    ? "bg-[var(--gold)]/15 border-[var(--gold)]"
                                                    : "bg-white/5 border-white/10 hover:border-white/20"
                                            }`}
                                        >
                                            <Icon size={18} className={on ? "text-[var(--gold-light)]" : "text-white/60"} />
                                            <span className={`text-[10px] font-mono uppercase tracking-widest ${on ? "text-[var(--gold-light)]" : "text-white/55"}`}>
                                                {c.label}
                                            </span>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                        <Field label="Tagline" testid="f-tagline" value={store.tagline} onChange={(v) => setStore({...store, tagline: v})} placeholder="Φρέσκα ψημένα κάθε μέρα" optional />
                        <Field label="Διεύθυνση" testid="f-address" value={store.address} onChange={(v) => setStore({...store, address: v})} placeholder="Αγ. Δημητρίου 5, Θεσσαλονίκη" />
                        <div className="grid grid-cols-2 gap-3">
                            <Field label="Lat" testid="f-lat" type="number" value={store.lat} onChange={(v) => setStore({...store, lat: v})} />
                            <Field label="Lng" testid="f-lng" type="number" value={store.lng} onChange={(v) => setStore({...store, lng: v})} />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <Field label="Παράδοση (λεπτά)" testid="f-min" type="number" value={store.delivery_minutes} onChange={(v) => setStore({...store, delivery_minutes: v})} />
                            <Field label="Delivery fee (€)" testid="f-fee" type="number" value={store.delivery_fee} onChange={(v) => setStore({...store, delivery_fee: v})} />
                        </div>
                    </section>
                )}

                {/* === STEP 2: MEDIA === */}
                {step === 2 && (
                    <section data-testid="step-media" className="space-y-4 animate-fade-up">
                        <p className="text-sm text-white/70">
                            Ανέβασε λογότυπο και κύρια φωτογραφία. Προαιρετικό αλλά ΔΥΝΑΤΟ — οι πελάτες θα σε αναγνωρίζουν.
                        </p>
                        <UploadCard
                            testid="upload-logo"
                            label="Λογότυπο"
                            sub="Τετράγωνο · ≤ 2MB"
                            value={media.logo_url}
                            onClick={() => fileLogoRef.current?.click()}
                            onClear={() => setMedia({...media, logo_url: ""})}
                        />
                        <input ref={fileLogoRef} type="file" accept="image/*" onChange={handleLogo} className="hidden" data-testid="logo-input" />
                        <UploadCard
                            testid="upload-image"
                            label="Hero φωτογραφία"
                            sub="Wide · ≤ 4MB"
                            value={media.image_url}
                            onClick={() => fileImageRef.current?.click()}
                            onClear={() => setMedia({...media, image_url: ""})}
                        />
                        <input ref={fileImageRef} type="file" accept="image/*" onChange={handleImage} className="hidden" data-testid="image-input" />
                    </section>
                )}

                {/* === STEP 3: MENU CSV === */}
                {step === 3 && (
                    <section data-testid="step-menu" className="space-y-4 animate-fade-up">
                        <p className="text-sm text-white/70">
                            Ανέβασε CSV με τα προϊόντα σου. Στήλες: <span className="font-mono text-[var(--gold-light)]">name, description, price, category, image_url</span>. Μπορείς να παραλείψεις και να τα προσθέσεις αργότερα από το Partner Dashboard.
                        </p>
                        <button
                            onClick={() => fileCsvRef.current?.click()}
                            data-testid="csv-upload-btn"
                            className="w-full glass-gold rounded-2xl border-2 border-dashed border-[var(--gold)]/30 hover:border-[var(--gold)]/60 transition py-8 flex flex-col items-center gap-2"
                        >
                            <Upload size={24} className="text-[var(--gold-light)]" />
                            <span className="text-sm text-white/85">{menuItems.length > 0 ? `${menuItems.length} προϊόντα έτοιμα` : "Ανέβασε CSV"}</span>
                            <span className="text-[10px] font-mono uppercase tracking-widest text-white/40">.csv</span>
                        </button>
                        <input ref={fileCsvRef} type="file" accept=".csv,text/csv" onChange={handleCsv} className="hidden" data-testid="csv-input" />
                        {csvError && <p className="text-xs text-[var(--gold-light)]" data-testid="csv-error">{csvError}</p>}
                        {menuItems.length > 0 && (
                            <div data-testid="csv-preview" className="space-y-1 max-h-60 overflow-y-auto rounded-2xl bg-white/5 border border-white/10 p-3">
                                {menuItems.slice(0, 10).map((it, i) => (
                                    <div key={i} className="flex items-center justify-between text-xs">
                                        <span className="text-white/85 truncate pr-2">{it.name}</span>
                                        <span className="text-[var(--gold-light)] font-mono">€{it.price.toFixed(2)}</span>
                                    </div>
                                ))}
                                {menuItems.length > 10 && (
                                    <p className="text-[10px] text-white/40 italic mt-2">+{menuItems.length - 10} ακόμη...</p>
                                )}
                            </div>
                        )}
                        <a
                            href="data:text/csv;charset=utf-8,name,description,price,category,image_url%0AΣουβλάκι χοιρινό,Με πίτα και πατάτες,3.50,mains,%0AΓύρος,Από κόντρα,4.00,mains,"
                            download="thesseats-template.csv"
                            className="block text-center text-[10px] font-mono uppercase tracking-widest text-white/50 hover:text-[var(--gold-light)] underline"
                        >
                            Κατέβασε template
                        </a>
                    </section>
                )}

                {/* === STEP 4: DONE === */}
                {step === STEPS.length - 1 && (
                    <section data-testid="step-done" className="text-center py-12 animate-fade-up">
                        <div className="mx-auto h-24 w-24 rounded-full bg-gradient-to-br from-[var(--gold)] to-[#c98e15] grid place-items-center shadow-[0_0_60px_rgba(244,179,57,0.5)]">
                            <Check size={48} strokeWidth={3} className="text-[#0B0B0F]" />
                        </div>
                        <h2 className="font-heading text-3xl mt-6" style={{ fontFamily: '"Cormorant Garamond", serif' }}>
                            <span className="gold-text">Καλώς ήρθες</span> στην οικογένεια!
                        </h2>
                        <p className="text-sm text-white/65 mt-2 max-w-md mx-auto">
                            Το κατάστημά σου είναι live στο Thess·Eats. Άνοιξε το Partner Dashboard για να δέχεσαι παραγγελίες σε real-time.
                        </p>
                        <div className="grid grid-cols-2 gap-2 mt-8 max-w-sm mx-auto">
                            <button onClick={() => navigate("/partner")} data-testid="goto-partner" className="gold-cta h-12 rounded-2xl font-mono uppercase tracking-widest text-xs font-bold">
                                Partner POS
                            </button>
                            <button onClick={() => navigate("/partner/analytics")} data-testid="goto-analytics" className="h-12 rounded-2xl border-2 border-[var(--gold)]/40 hover:border-[var(--gold)] font-mono uppercase tracking-widest text-xs text-[var(--gold-light)]">
                                Στατιστικά
                            </button>
                        </div>
                    </section>
                )}
            </div>

            {/* CTA FOOTER */}
            {step < STEPS.length - 1 && (
                <div className="fixed bottom-0 left-0 right-0 z-30 cyber-bg/95 backdrop-blur-xl border-t border-white/5 px-5 py-4">
                    <button
                        onClick={goNext}
                        disabled={!canNext() || submitting}
                        data-testid="signup-next"
                        className="gold-cta w-full h-14 rounded-2xl flex items-center justify-center gap-2 font-mono uppercase tracking-widest text-sm font-bold disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                        {submitting ? (
                            <Loader2 className="animate-spin" size={18} />
                        ) : step === 3 ? (
                            <><Sparkles size={16} /> Ολοκλήρωση & Go-Live</>
                        ) : (
                            <>Συνέχεια <ArrowRight size={16} strokeWidth={3} /></>
                        )}
                    </button>
                </div>
            )}
        </div>
    );
};

const Field = ({ label, testid, value, onChange, placeholder, type = "text", optional }) => (
    <div>
        <label className="text-[10px] font-mono uppercase tracking-widest text-white/50 flex items-center gap-2 mb-1.5">
            {label}
            {optional && <span className="text-white/30">(προαιρετικό)</span>}
        </label>
        <input
            type={type}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            data-testid={testid}
            className="w-full h-12 rounded-2xl bg-white/5 border border-white/10 px-4 text-sm text-white placeholder:text-white/30 outline-none focus:border-[var(--gold)]/60 focus:bg-white/10 transition"
        />
    </div>
);

const UploadCard = ({ testid, label, sub, value, onClick, onClear }) => (
    <div data-testid={testid} className="glass-gold rounded-2xl border-2 border-dashed border-white/15 hover:border-[var(--gold)]/40 transition p-3 flex items-center gap-3">
        {value ? (
            <img src={value} alt={label} className="w-16 h-16 rounded-xl object-cover" />
        ) : (
            <div className="w-16 h-16 rounded-xl bg-white/5 grid place-items-center">
                <ImagePlus size={20} className="text-white/40" />
            </div>
        )}
        <div className="flex-1 min-w-0">
            <p className="text-sm text-white">{label}</p>
            <p className="text-[10px] font-mono uppercase tracking-widest text-white/40">{sub}</p>
        </div>
        {value ? (
            <button onClick={onClear} className="h-9 w-9 grid place-items-center rounded-full bg-white/10 hover:bg-white/20 transition">
                <X size={14} />
            </button>
        ) : (
            <button onClick={onClick} className="h-9 px-3 rounded-full bg-[var(--gold)]/15 hover:bg-[var(--gold)]/25 text-[10px] font-mono uppercase tracking-widest text-[var(--gold-light)]">
                Upload
            </button>
        )}
    </div>
);

export default PartnerSignup;
